import { useState } from "react";

import type {
  FeedbackReason,
  FeedbackResponse,
  FeedbackSubmission,
} from "../../shared/api/client";
import { useT, type MessageKey } from "../../shared/i18n";
import { Banner, Button, Card, Checkbox, Chip, Icons } from "../../shared/ui";

const REASONS: readonly FeedbackReason[] = [
  "wrong_action",
  "claimed_but_not_done",
  "wrong_data",
  "wrong_language",
  "too_slow",
  "other",
];

type Step = "idle" | "reason" | "submitted";

/**
 * Verdict controls for a terminal assistant reply (ADR 0216).
 *
 * A positive verdict is sent at once. A negative one opens the reason chips
 * and the consent box; the consent is never pre-ticked and `share_for_review`
 * is in the request only when it is. The control shows the server response,
 * not an optimistic state.
 */
export function FeedbackBar({
  onSubmit,
}: {
  onSubmit: (body: FeedbackSubmission) => Promise<FeedbackResponse>;
}) {
  const t = useT();
  const [step, setStep] = useState<Step>("idle");
  const [reason, setReason] = useState<FeedbackReason | null>(null);
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const send = async (body: FeedbackSubmission) => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await onSubmit(body);
      setStep("submitted");
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep("idle");
    setReason(null);
    setShare(false);
    setFailed(false);
  };

  if (step === "submitted") {
    return (
      <Card>
        <div className="flex items-center justify-between gap-3">
          <span
            role="status"
            className="flex min-w-0 items-center gap-2 text-callout text-fg-muted"
          >
            <Icons.Check size={18} className="shrink-0 text-success" />
            {t("assistant.feedback.thanks")}
          </span>
          <Button variant="tinted" size="sm" onClick={reset}>
            {t("assistant.feedback.change")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div
        role="group"
        aria-label={t("assistant.feedback.title")}
        className="flex flex-col gap-3"
      >
        {failed && (
          <Banner tone="error">{t("assistant.feedback.error")}</Banner>
        )}
        {step === "idle" ? (
          <>
            <span className="text-headline text-fg">
              {t("assistant.feedback.title")}
            </span>
            <div className="flex gap-2 [&>*]:flex-1">
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  void send({ verdict: "up" });
                }}
              >
                <Icons.ThumbUp size={18} />
                {t("assistant.feedback.up")}
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setStep("reason");
                }}
              >
                <Icons.ThumbDown size={18} />
                {t("assistant.feedback.down")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <span className="text-headline text-fg">
              {t("assistant.feedback.reasonTitle")}
            </span>
            <div className="flex flex-wrap gap-2">
              {REASONS.map((item) => (
                <Chip
                  key={item}
                  selected={reason === item}
                  onClick={() => {
                    setReason(reason === item ? null : item);
                  }}
                >
                  {t(`assistant.feedback.reason.${item}` as MessageKey)}
                </Chip>
              ))}
            </div>
            <Checkbox
              label={t("assistant.feedback.consent")}
              checked={share}
              onCheckedChange={setShare}
            />
            <p className="text-caption text-fg-subtle">
              {t("assistant.feedback.consentHint")}
            </p>
            <div className="flex gap-2 [&>*]:flex-1">
              <Button variant="secondary" disabled={busy} onClick={reset}>
                {t("assistant.feedback.back")}
              </Button>
              <Button
                loading={busy}
                onClick={() => {
                  void send({
                    verdict: "down",
                    ...(reason ? { reason } : {}),
                    ...(share ? { share_for_review: true as const } : {}),
                  });
                }}
              >
                {t("assistant.feedback.send")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
